import { useCallback, useEffect, useRef, useState } from 'react';

const REFRESH_INTERVAL_MS = 60000;

function startOfToday() {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now.toISOString();
}

/**
 * Единая «живая» сводка для шапки админки: сегодняшние запросы/ошибки,
 * зависшие в очереди запросы и простои за 30 дней.
 *
 * Обновляется раз в минуту, пока вкладка видима, и отдаёт данные и вкладке
 * «Обзор», и бейджам на табах — один запрос на всю панель.
 */
export function useAdminLiveSummary({ getOverview, getPendingAged, getDowntime, enabled = true }) {
  const [overview, setOverview] = useState(null);
  const [pendingAged, setPendingAged] = useState(0);
  const [downtime, setDowntime] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const inflightRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!enabled || inflightRef.current) return;
      inflightRef.current = true;
      if (!silent) setLoading(true);
      setError(null);

      const todayParams = { dateFrom: startOfToday(), dateTo: new Date().toISOString() };

      try {
        const [overviewRes, pendingRes, downtimeRes] = await Promise.all([
          getOverview(todayParams),
          getPendingAged({ minutes: 30, days: 30 }),
          getDowntime({ days: 30 }),
        ]);
        if (!mountedRef.current) return;
        setOverview(overviewRes.overview || null);
        setPendingAged(pendingRes.count || 0);
        setDowntime(downtimeRes.downtime || null);
        setUpdatedAt(new Date().toISOString());
      } catch (err) {
        if (!mountedRef.current) return;
        setError(err?.message || 'Не удалось обновить сводку');
      } finally {
        inflightRef.current = false;
        if (mountedRef.current) setLoading(false);
      }
    },
    [enabled, getOverview, getPendingAged, getDowntime]
  );

  useEffect(() => {
    if (!enabled) return undefined;
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      load({ silent: true });
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [enabled, load]);

  return { overview, pendingAged, downtime, loading, error, updatedAt, refresh: load };
}
